import React from 'react';
import { useLocation } from 'react-router-dom';
import { CheckIcon, BellIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';

export default function ThankYouPage() {
  const { state } = useLocation();
  const assessmentTitle = state?.assessmentTitle ?? 'Assessment';
  const participantName = state?.participantName ?? '';
  const autoSubmit = state?.autoSubmit ?? false;

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-primary-50 to-secondary-50">
      {/* Header */}
      <header className="flex items-center gap-2 px-6 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600">
          <SparklesIcon className="h-4 w-4 text-white" />
        </div>
        <span className="text-base font-bold font-heading text-neutral-900 tracking-tight">
          FounderMind <span className="text-primary-600">AI</span>
        </span>
      </header>

      {/* Main content */}
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-slide-up">
          <div className="rounded-card bg-white p-8 shadow-modal text-center">
            {/* Checkmark icon */}
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success-100 ring-8 ring-success-50">
              <CheckIcon className="h-10 w-10 text-success-600 stroke-[2.5]" />
            </div>

            {/* Heading */}
            <h1 className="mb-3 text-2xl font-bold font-heading text-neutral-900">
              Assessment Submitted!
            </h1>

            {/* Personalised message */}
            <p className="mb-2 text-base text-neutral-700 leading-relaxed">
              {participantName ? (
                <>
                  Thank you, <span className="font-semibold text-primary-700">{participantName}</span>!
                </>
              ) : (
                'Thank you!'
              )}{' '}
              Your responses have been recorded and will be reviewed by our team.
            </p>

            {/* Assessment title */}
            <p className="mb-6 text-sm text-neutral-500">
              <span className="font-medium text-neutral-600">{assessmentTitle}</span>
            </p>

            {/* Auto-submit notice */}
            {autoSubmit && (
              <div className="mb-6 rounded-input bg-warning-50 border border-warning-200 px-4 py-3 text-sm text-warning-700">
                Your assessment was automatically submitted when the time limit was reached.
              </div>
            )}

            {/* Divider */}
            <div className="my-6 h-px bg-neutral-100" />

            {/* What happens next */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50">
                <BellIcon className="h-5 w-5 text-primary-600" />
              </div>
              <p className="text-sm font-medium text-neutral-700">You'll be notified of your results</p>
              <p className="text-xs text-neutral-400 leading-relaxed text-center max-w-xs">
                Our team will review your submission and you'll receive feedback through the contact information you provided.
              </p>
            </div>
          </div>

          {/* Encouragement blurb */}
          <p className="mt-6 text-center text-xs text-neutral-400">
            Great work completing the assessment. You can safely close this window.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-5 text-center">
        <div className="inline-flex items-center gap-1.5 text-xs text-neutral-400">
          <SparklesIcon className="h-3.5 w-3.5 text-primary-400" />
          Powered by{' '}
          <span className="font-semibold text-neutral-500">FounderMind AI</span>
        </div>
      </footer>
    </div>
  );
}
